<template>
  <div class="area-boundary-editor">
    <div class="editor-header">
      <h3>区域边界编辑</h3>
      <div class="editor-tips">
        <Icon type="ios-information-circle" />
        <span>点击地图绘制边界点，至少需要3个点，双击完成绘制</span>
      </div>
    </div>
    
    <div id="boundaryMap" class="map-container"></div>
    
    <div class="editor-controls">
      <Button 
        type="primary" 
        :disabled="isDrawing" 
        @click="startDrawing"
        icon="ios-create"
      >
        开始绘制
      </Button>
      <Button 
        type="success" 
        :disabled="!isDrawing || points.length < 3" 
        @click="finishDrawing"
        icon="ios-checkmark"
      >
        完成绘制 ({{ points.length }}个点)
      </Button>
      <Button 
        :disabled="!polygon" 
        @click="editBoundary"
        icon="ios-create-outline"
      >
        编辑边界
      </Button>
      <Button 
        :disabled="!polygon" 
        @click="clearBoundary"
        icon="ios-trash"
      >
        清除
      </Button>
      <Button 
        @click="cancel"
        icon="ios-close"
      >
        取消
      </Button>
    </div>
    
    <div v-if="points.length > 0" class="points-list">
      <div class="points-header">边界点列表：</div>
      <div class="points-content">
        <Tag 
          v-for="(point, index) in points" 
          :key="index"
          closable
          @on-close="removePoint(index)"
          style="margin: 4px;"
        >
          点{{ index + 1 }}: ({{ point[0].toFixed(6) }}, {{ point[1].toFixed(6) }})
        </Tag>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AreaBoundaryEditor',
  props: {
    center: {
      type: Array,
      default: () => [116.397428, 39.90923] // 默认北京
    },
    boundary: {
      type: Array,
      default: () => []
    },
    zoom: {
      type: Number,
      default: 13
    }
  },
  data() {
    return {
      map: null,
      polygon: null,
      polygonEditor: null,
      isDrawing: false,
      points: [],
      clickHandler: null,
      mapLoaded: false
    }
  },
  watch: {
    boundary: {
      handler(newBoundary, oldBoundary) {
        // 避免重复加载相同的数据
        if (JSON.stringify(newBoundary) === JSON.stringify(oldBoundary)) {
          return
        }
        
        console.log('AreaBoundaryEditor - boundary变化:', newBoundary, 'mapLoaded:', this.mapLoaded)
        
        // 检查是否是有效的边界数据
        const isValidBoundary = newBoundary && 
                                Array.isArray(newBoundary) && 
                                newBoundary.length > 0 &&
                                newBoundary.some(point => Array.isArray(point) && point.length >= 2)
        
        if (isValidBoundary) {
          // 等待地图加载完成后再加载边界
          if (this.mapLoaded) {
            this.$nextTick(() => {
              // 防止重复加载
              if (this.polygon && this.points.length > 0) {
                const currentPoints = this.points
                const newPoints = newBoundary.slice(0, -1) // 移除闭合点
                if (JSON.stringify(currentPoints) === JSON.stringify(newPoints)) {
                  console.log('边界数据未变化，跳过加载')
                  return
                }
              }
              this.loadBoundary(newBoundary)
            })
          }
        } else {
          // 如果边界为空或无效，清除当前边界（silent=false，需要通知父组件）
          if (this.mapLoaded && this.polygon) {
            this.clearBoundary(false)
          }
        }
      },
      immediate: false,
      deep: true
    },
    center: {
      handler(newCenter) {
        if (this.map && newCenter && newCenter.length === 2) {
          this.map.setCenter(newCenter)
        }
      },
      immediate: false
    }
  },
  mounted() {
    // 延迟初始化，确保DOM已渲染
    this.$nextTick(() => {
      setTimeout(() => {
        this.loadAMapScript()
      }, 300)
    })
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    // 加载高德地图脚本
    async loadAMapScript() {
      try {
        await this.ensureAMapLoaded()
        this.initMap()
      } catch (e) {
        const msg = (e && e.message) ? e.message : '地图加载失败：请检查高德地图Key/安全密钥/域名白名单/网络'
        console.error('高德地图加载失败:', e)
        this.$Message.error(msg)
      }
    },

    ensureAMapLoaded() {
      if (window.AMap) return Promise.resolve()

      const AMAP_SCRIPT_ID = 'amap-jsapi'
      const key = (process.env.VUE_APP_AMAP_KEY || '').trim()
      const securityJsCode = (process.env.VUE_APP_AMAP_SECURITY_JS_CODE || '').trim()

      if (!key) {
        return Promise.reject(new Error('地图加载失败：未配置高德Key（请设置 VUE_APP_AMAP_KEY）'))
      }

      // 如果启用了“安全密钥”，必须在加载JSAPI前设置
      if (securityJsCode && !window._AMapSecurityConfig) {
        window._AMapSecurityConfig = { securityJsCode }
      }

      const plugin = [
        'AMap.Geocoder',
        'AMap.Autocomplete',
        'AMap.PlaceSearch',
        'AMap.Scale',
        'AMap.ToolBar',
        'AMap.PolygonEditor'
      ].join(',')

      return new Promise((resolve, reject) => {
        const existing = document.getElementById(AMAP_SCRIPT_ID)
        if (existing) {
          const start = Date.now()
          const timeoutMs = 15000
          const timer = setInterval(() => {
            if (window.AMap) {
              clearInterval(timer)
              resolve()
              return
            }
            if (Date.now() - start >= timeoutMs) {
              clearInterval(timer)
              reject(new Error('地图加载失败：高德脚本加载超时（请检查网络/域名白名单/Key/安全密钥）'))
            }
          }, 200)
          return
        }

        const script = document.createElement('script')
        script.id = AMAP_SCRIPT_ID
        script.type = 'text/javascript'
        script.async = true
        script.defer = true

        const cbName = '__initAMapBoundaryEditor__'
        window[cbName] = () => {
          try {
            delete window[cbName]
          } catch (err) {
            window[cbName] = undefined
          }
          if (window.AMap) resolve()
          else reject(new Error('地图加载失败：高德脚本回调已触发但 AMap 未挂载（Key/安全密钥可能不匹配）'))
        }

        script.onerror = () => {
          try {
            delete window[cbName]
          } catch (err) {
            window[cbName] = undefined
          }
          reject(new Error('地图加载失败：高德脚本请求失败（可能被拦截/网络不可达/域名白名单不通过）'))
        }

        script.src = `https://webapi.amap.com/maps?v=1.4.15&key=${encodeURIComponent(key)}&plugin=${encodeURIComponent(plugin)}&callback=${encodeURIComponent(cbName)}`
        document.head.appendChild(script)
      })
    },

    // 初始化地图
    initMap() {
      if (!window.AMap) {
        console.error('高德地图API未加载')
        return
      }

      // 检查地图容器是否存在
      const mapContainer = document.getElementById('boundaryMap')
      if (!mapContainer) {
        console.error('地图容器不存在，延迟初始化')
        setTimeout(() => {
          this.initMap()
        }, 200)
        return
      }

      // 如果地图已初始化，先销毁
      if (this.map) {
        try {
          this.map.destroy()
        } catch (e) {
          console.warn('销毁旧地图失败:', e)
        }
        this.map = null
      }

      try {
        // 验证并格式化中心点坐标
        let centerPoint = this.center
        
        // 检查是否为函数（计算属性可能返回函数）
        if (typeof centerPoint === 'function') {
          console.warn('中心点是函数，尝试调用:', centerPoint)
          try {
            centerPoint = centerPoint()
          } catch (e) {
            console.warn('调用中心点函数失败，使用默认值:', e)
            centerPoint = [116.397428, 39.90923] // 默认北京
          }
        }
        
        if (!Array.isArray(centerPoint) || centerPoint.length !== 2) {
          console.warn('中心点格式不正确，使用默认值:', centerPoint, typeof centerPoint)
          centerPoint = [116.397428, 39.90923] // 默认北京
        } else {
          // 确保是数字类型
          const lng = parseFloat(centerPoint[0])
          const lat = parseFloat(centerPoint[1])
          if (isNaN(lng) || isNaN(lat)) {
            console.warn('中心点坐标不是有效数字，使用默认值:', centerPoint)
            centerPoint = [116.397428, 39.90923] // 默认北京
          } else {
            centerPoint = [lng, lat]
          }
        }
        
        this.map = new window.AMap.Map('boundaryMap', {
          zoom: this.zoom,
          center: centerPoint,
          mapStyle: 'amap://styles/normal'
        })

        // 添加地图控件（检查控件是否可用）
        try {
          if (window.AMap.Scale && typeof window.AMap.Scale === 'function') {
            this.map.addControl(new window.AMap.Scale())
          } else {
            console.warn('AMap.Scale控件不可用，跳过添加')
          }
        } catch (e) {
          console.warn('添加Scale控件失败:', e)
        }
        
        try {
          if (window.AMap.ToolBar && typeof window.AMap.ToolBar === 'function') {
            this.map.addControl(new window.AMap.ToolBar({
              position: 'RT'
            }))
          } else {
            console.warn('AMap.ToolBar控件不可用，跳过添加')
          }
        } catch (e) {
          console.warn('添加ToolBar控件失败:', e)
        }

        this.mapLoaded = true

        // 地图加载完成后，如果有边界数据，加载显示
        this.map.on('complete', () => {
          console.log('地图加载完成，检查边界数据:', this.boundary)
          if (this.boundary && Array.isArray(this.boundary) && this.boundary.length > 0) {
            this.$nextTick(() => {
              this.loadBoundary(this.boundary)
            })
          }
        })
        
        // 如果边界数据已经存在，立即加载（不等待complete事件）
        if (this.boundary && Array.isArray(this.boundary) && this.boundary.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.loadBoundary(this.boundary)
            }, 500) // 延迟一点确保地图完全初始化
          })
        }
      } catch (error) {
        console.error('地图初始化失败:', error)
        this.$Message.error('地图初始化失败')
      }
    },

    // 加载已有边界
    loadBoundary(coordinates) {
      if (!this.map) {
        console.warn('地图未初始化，无法加载边界')
        return
      }

      if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 3) {
        console.warn('边界数据无效:', coordinates)
        return
      }

      console.log('加载边界数据，点数:', coordinates.length)

      // 清除旧边界（silent=true，不触发boundary-change事件，避免循环）
      this.clearBoundary(true)

      // 处理边界数据：如果最后一个点等于第一个点，移除最后一个点（闭合点）
      let boundaryPoints = [...coordinates]
      if (boundaryPoints.length > 0) {
        const firstPoint = boundaryPoints[0]
        const lastPoint = boundaryPoints[boundaryPoints.length - 1]
        // 检查是否是闭合的（最后一个点等于第一个点）
        if (boundaryPoints.length > 1 && 
            Math.abs(firstPoint[0] - lastPoint[0]) < 0.000001 && 
            Math.abs(firstPoint[1] - lastPoint[1]) < 0.000001) {
          boundaryPoints = boundaryPoints.slice(0, -1)
        }
      }

      // 设置点数组（不包含闭合点）
      this.points = boundaryPoints.map(coord => {
        if (Array.isArray(coord) && coord.length >= 2) {
          return [parseFloat(coord[0]), parseFloat(coord[1])]
        }
        return coord
      })

      // 创建闭合路径（用于显示）
      const closedPath = [...this.points, this.points[0]].map(
        coord => new window.AMap.LngLat(coord[0], coord[1])
      )

      // 创建多边形
      this.polygon = new window.AMap.Polygon({
        path: closedPath,
        strokeColor: '#1890ff',
        strokeWeight: 2,
        fillColor: '#1890ff',
        fillOpacity: 0.2,
        strokeStyle: 'solid',
        strokeOpacity: 0.8,
        editable: false
      })

      this.map.add(this.polygon)
      this.map.setFitView([this.polygon], false, [50, 50, 50, 50])
      
      console.log('边界加载完成，点数:', this.points.length)

      // 创建编辑器
      this.createEditor()
    },

    // 开始绘制
    startDrawing() {
      if (!this.map) {
        this.$Message.warning('地图未加载完成，请稍候')
        return
      }

      this.clearBoundary()
      this.isDrawing = true
      this.points = []

      // 绑定地图点击事件
      this.clickHandler = this.map.on('click', (e) => {
        const lng = e.lnglat.getLng()
        const lat = e.lnglat.getLat()
        this.addPoint([lng, lat])
      })

      this.$Message.info('点击地图添加边界点，至少需要3个点')
    },

    // 添加点
    addPoint(point) {
      this.points.push(point)

      // 如果已有多边形，更新它
      if (this.polygon) {
        this.updatePolygon()
      } else if (this.points.length >= 2) {
        // 至少2个点才能创建多边形
        this.createPolygon()
      }
    },

    // 移除点
    removePoint(index) {
      if (this.points.length <= 3) {
        this.$Message.warning('至少需要3个点')
        return
      }

      this.points.splice(index, 1)
      this.updatePolygon()
    },

    // 创建多边形
    createPolygon() {
      if (!this.map || this.points.length < 2) return

      // 创建路径（闭合）
      const path = [...this.points, this.points[0]].map(
        coord => new window.AMap.LngLat(coord[0], coord[1])
      )

      this.polygon = new window.AMap.Polygon({
        path: path,
        strokeColor: '#1890ff',
        strokeWeight: 2,
        fillColor: '#1890ff',
        fillOpacity: 0.2,
        strokeStyle: 'solid',
        strokeOpacity: 0.8,
        editable: false
      })

      this.map.add(this.polygon)
    },

    // 更新多边形
    updatePolygon() {
      if (!this.polygon || this.points.length < 2) return

      const path = [...this.points, this.points[0]].map(
        coord => new window.AMap.LngLat(coord[0], coord[1])
      )

      this.polygon.setPath(path)
    },

    // 完成绘制
    finishDrawing() {
      if (this.points.length < 3) {
        this.$Message.warning('至少需要3个点才能构成多边形')
        return
      }

      // 解除点击事件
      if (this.clickHandler) {
        this.map.off('click', this.clickHandler)
        this.clickHandler = null
      }

      this.isDrawing = false

      // 确保闭合
      const closedPoints = [...this.points, this.points[0]]

      // 创建编辑器
      this.createEditor()

      // 通知父组件
      this.$emit('boundary-change', closedPoints)
      this.$Message.success('边界绘制完成，可以拖拽编辑边界点')
    },

    // 创建编辑器
    createEditor() {
      if (!this.polygon || !window.AMap.PolygonEditor) {
        // 如果PolygonEditor不可用，使用简单编辑
        this.polygon.setOptions({ editable: true })
        return
      }

      // 销毁旧编辑器
      if (this.polygonEditor) {
        this.polygonEditor.close()
      }

      // 创建新编辑器
      this.polygonEditor = new window.AMap.PolygonEditor(this.map, this.polygon)
      this.polygonEditor.open()

      // 监听编辑事件
      this.polygon.on('adjust', () => {
        const path = this.polygon.getPath()
        this.points = path.map(point => [point.getLng(), point.getLat()])
        // 移除闭合点
        if (this.points.length > 0 && 
            this.points[0][0] === this.points[this.points.length - 1][0] &&
            this.points[0][1] === this.points[this.points.length - 1][1]) {
          this.points.pop()
        }
      })

      this.polygon.on('end', () => {
        const path = this.polygon.getPath()
        this.points = path.map(point => [point.getLng(), point.getLat()])
        const closedPoints = [...this.points, this.points[0]]
        this.$emit('boundary-change', closedPoints)
      })
    },

    // 编辑边界
    editBoundary() {
      if (!this.polygon) {
        this.$Message.warning('请先绘制边界')
        return
      }

      this.createEditor()
      this.$Message.info('可以拖拽边界点进行编辑')
    },

    // 清除边界
    clearBoundary(silent = false) {
      if (this.polygon) {
        this.map.remove(this.polygon)
        this.polygon = null
      }

      if (this.polygonEditor) {
        this.polygonEditor.close()
        this.polygonEditor = null
      }

      this.points = []
      this.isDrawing = false

      if (this.clickHandler) {
        this.map.off('click', this.clickHandler)
        this.clickHandler = null
      }

      // 只有在用户主动清除时才通知父组件，加载时不清除（silent=true）
      if (!silent) {
        this.$emit('boundary-change', [])
      }
    },

    // 取消
    cancel() {
      this.clearBoundary()
      this.$emit('cancel')
    },

    // 清理资源
    cleanup() {
      if (this.clickHandler && this.map) {
        this.map.off('click', this.clickHandler)
      }

      if (this.polygonEditor) {
        this.polygonEditor.close()
      }

      if (this.polygon && this.map) {
        this.map.remove(this.polygon)
      }

      if (this.map) {
        this.map.destroy()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.area-boundary-editor {
  .editor-header {
    margin-bottom: 16px;
    
    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 500;
    }
    
    .editor-tips {
      display: flex;
      align-items: center;
      color: #999;
      font-size: 12px;
      
      i {
        margin-right: 4px;
      }
    }
  }

  .map-container {
    width: 100%;
    height: 500px;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 16px;
  }

  .editor-controls {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .points-list {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    
    .points-header {
      font-weight: 500;
      margin-bottom: 8px;
      color: #333;
    }
    
    .points-content {
      max-height: 150px;
      overflow-y: auto;
    }
  }
}
</style>

